import Phaser from "phaser";
import type { TextSegment } from "@pix/script";
import type { PlayerState } from "../runtime/playerState";

/**
 * Phaser Text を 1 文字ずつ Container に並べてリッチ装飾を実現するレンダラ。
 *
 * 対応:
 *  - [wait time=N] [br] [var name=PATH] [ruby base text] [color] [size] [shake]
 *  - エッジ(stroke) + シャドウ
 *  - kinsoku(行頭/行末禁則)
 *  - 会話文の頭への自動スペース挿入
 *  - [size=±N] のベースライン揃え
 *  - 句読点後の自動ポーズ(4×)
 */

interface StyleState {
  color: string;
  fontSize: number;
  shake: boolean;
}

const RUBY_SIZE = 0.6;
const SIZE_MULTIPLIERS: Record<number, number> = {
  [-2]: 0.5,
  [-1]: 0.75,
  0: 1.0,
  1: 1.4,
  2: 1.8,
};
const PUNCTUATION_PAUSE = "、。!?!?・…,.";
const LINE_START_FORBIDDEN = "、。,，．！?!?・…)).)」』】〕〉》〙〛”'";
const LINE_END_FORBIDDEN = "「『(([【〔〈《〘〚“'";
const CONVERSATION_OPENERS = "「『(([【〔〈《〘〚";

export interface MessageRendererOptions {
  scene: Phaser.Scene;
  /** container の左上(world座標) */
  x: number;
  y: number;
  /** 行内最大幅(px、これを超えると折返し) */
  maxWidth: number;
  /** 通常時の文字サイズ */
  baseSize: number;
  /** 行送り */
  lineHeight: number;
  /** デフォルト文字色 */
  baseColor: string;
  /** フォント */
  fontFamily: string;
  /** ルビの色(未指定なら現在の文字色を継承) */
  rubyColor?: string;
  /** ルビ用の上余白(初行で見切れないよう、cursorY 初期値に加算) */
  topPad?: number;
  /** エッジ(縁取り) */
  enableEdge?: boolean;
  edgeColor?: string;
  edgeThickness?: number;
  /** シャドウ(影) */
  enableShadow?: boolean;
  shadowColor?: string;
  shadowOffset?: number;
  /** 会話文の頭(「『(【...で始まる)に自動で全角スペースを入れる */
  addConversationSpace?: boolean;
}

interface PlacedChar {
  ch: string;
  text: Phaser.GameObjects.Text;
  width: number;
}

export class MessageRenderer {
  private container: Phaser.GameObjects.Container;
  private cursorX = 0;
  private cursorY = 0;
  private styleStack: StyleState[] = [];
  private current: StyleState;
  private cancel = false;
  private fastFwd = false;
  private running = false;
  private linePlaced: PlacedChar[] = [];
  private kinsokuCount = 0;

  constructor(
    private opts: MessageRendererOptions,
    private getVariable: (path: string) => unknown,
    private state: PlayerState,
  ) {
    this.container = opts.scene.add.container(opts.x, opts.y);
    this.current = {
      color: opts.baseColor,
      fontSize: opts.baseSize,
      shake: false,
    };
  }

  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  isRunning(): boolean {
    return this.running;
  }

  fastForward(): void {
    this.fastFwd = true;
  }

  cancelAndClear(): void {
    this.cancel = true;
    this.clearContent();
  }

  private clearContent(): void {
    this.container.removeAll(true);
    this.cursorX = 0;
    this.cursorY = this.opts.topPad ?? 14;
    this.styleStack = [];
    this.linePlaced = [];
    this.kinsokuCount = 0;
    this.current = {
      color: this.opts.baseColor,
      fontSize: this.opts.baseSize,
      shake: false,
    };
  }

  async render(segments: TextSegment[]): Promise<void> {
    this.cancel = false;
    this.fastFwd = false;
    this.running = true;
    this.clearContent();

    try {
      const speedMs = this.state.config.textSpeed;
      const skip = this.state.skipMode;

      // 会話文の頭にスペースを自動挿入
      if (this.shouldAddLeadSpace(segments)) {
        this.placeChar("　"); // 全角スペース
      }

      for (const seg of segments) {
        if (this.cancel) return;
        if (seg.kind === "text") {
          await this.typeOut(seg.value, speedMs, skip);
        } else if (seg.kind === "tag" && !seg.closing) {
          await this.handleOpenTag(seg.name, seg.attrs, speedMs, skip);
        } else if (seg.kind === "tag" && seg.closing) {
          this.handleCloseTag();
        }
      }
    } finally {
      this.running = false;
    }
  }

  plain(segments: TextSegment[]): string {
    let out = "";
    for (const seg of segments) {
      if (seg.kind === "text") {
        out += seg.value;
      } else if (seg.kind === "tag" && !seg.closing) {
        if (seg.name === "br") out += "\n";
        else if (seg.name === "var") {
          const path = seg.attrs["name"] ?? seg.attrs["value"] ?? seg.attrs["_arg0"] ?? "";
          out += String(this.getVariable(path) ?? "");
        } else if (seg.name === "ruby") {
          const base = seg.attrs["base"] ?? seg.attrs["_arg0"] ?? "";
          const text = seg.attrs["text"] ?? seg.attrs["_arg1"] ?? "";
          out += text ? `${base}(${text})` : base;
        }
      }
    }
    return out;
  }

  private async typeOut(str: string, speedMs: number, skip: boolean): Promise<void> {
    for (const ch of [...str]) {
      if (this.cancel) return;
      this.placeChar(ch);
      if (!this.fastFwd && !skip && speedMs > 0) {
        const pause = PUNCTUATION_PAUSE.includes(ch) ? speedMs * 4 : speedMs;
        await sleep(pause);
      }
    }
  }

  /** 1 文字を配置(kinsoku 含む) */
  private placeChar(ch: string): { x: number; y: number; w: number } | null {
    if (ch === "\n") {
      this.newline();
      return null;
    }

    const fontSize = this.current.fontSize;
    const t = this.makeText(ch);
    const w = t.width;
    const yOffset = this.opts.baseSize - fontSize;

    // kinsoku 適用
    if (this.cursorX > 0 && this.cursorX + w > this.opts.maxWidth) {
      // ラップが必要
      // 1. 直前文字が行末禁則 (開きカッコ系) → 直前を次行に押し出して current も次行
      const last = this.linePlaced[this.linePlaced.length - 1];
      if (last && LINE_END_FORBIDDEN.includes(last.ch) && this.kinsokuCount < 1) {
        this.kinsokuCount++;
        this.linePlaced.pop();
        this.cursorX -= last.width;
        this.newline();
        last.text.setPosition(
          this.cursorX,
          this.cursorY + (this.opts.baseSize - this.getCharFontSize(last.text)),
        );
        this.linePlaced.push(last);
        this.cursorX += last.width;
        // current 文字を続けて配置
      } else if (LINE_START_FORBIDDEN.includes(ch) && this.kinsokuCount < 1) {
        // 2. 現在文字が行頭禁則 (閉じカッコ・句読点) → 現在行に追加(オーバーフロー許容)
        this.kinsokuCount++;
        // wrap せずに現在行へ
      } else {
        // 3. 通常ラップ
        this.newline();
      }
    }

    t.setPosition(this.cursorX, this.cursorY + yOffset);
    this.decorate(t);
    this.container.add(t);
    this.linePlaced.push({ ch, text: t, width: w });
    this.cursorX += w;
    return { x: this.cursorX - w, y: this.cursorY + yOffset, w };
  }

  private makeText(ch: string): Phaser.GameObjects.Text {
    return this.opts.scene.add.text(0, 0, ch, {
      fontFamily: this.opts.fontFamily,
      fontSize: `${this.current.fontSize}px`,
      color: this.current.color,
    });
  }

  /** Edge / Shadow / Shake を適用 */
  private decorate(t: Phaser.GameObjects.Text): void {
    if (this.opts.enableEdge !== false) {
      const color = this.opts.edgeColor ?? "#1a1a2a";
      const thickness = this.opts.edgeThickness ?? 3;
      t.setStroke(color, thickness);
    }
    if (this.opts.enableShadow) {
      const sc = this.opts.shadowColor ?? "#000000";
      const off = this.opts.shadowOffset ?? 2;
      t.setShadow(off, off, sc, 0, false, true);
    }
    if (this.current.shake) {
      this.applyShake(t);
    }
  }

  private getCharFontSize(t: Phaser.GameObjects.Text): number {
    // Phaser Text の style.fontSize は文字列なので number に変換
    const fs = t.style.fontSize;
    if (typeof fs === "number") return fs;
    return parseInt(String(fs), 10) || this.opts.baseSize;
  }

  private newline(): void {
    this.cursorX = 0;
    this.cursorY += this.opts.lineHeight;
    this.linePlaced = [];
    this.kinsokuCount = 0;
  }

  private applyShake(target: Phaser.GameObjects.Text): void {
    const origX = target.x;
    const origY = target.y;
    this.opts.scene.tweens.add({
      targets: target,
      x: origX + 1.5,
      y: origY - 1,
      yoyo: true,
      duration: 90,
      repeat: -1,
    });
  }

  private async handleOpenTag(
    name: string,
    attrs: Record<string, string>,
    speedMs: number,
    skip: boolean,
  ): Promise<void> {
    switch (name) {
      case "br":
        this.newline();
        return;
      case "wait": {
        const ms = Number(attrs["time"] ?? attrs["value"] ?? attrs["_arg0"] ?? 200);
        if (!this.fastFwd && !skip) {
          await sleep(ms);
        }
        return;
      }
      case "var": {
        const path = attrs["name"] ?? attrs["value"] ?? attrs["_arg0"] ?? "";
        const value = String(this.getVariable(path) ?? "");
        await this.typeOut(value, speedMs, skip);
        return;
      }
      case "ruby":
        await this.placeRuby(
          attrs["base"] ?? attrs["_arg0"] ?? "",
          attrs["text"] ?? attrs["_arg1"] ?? "",
          speedMs,
          skip,
        );
        return;
      case "color":
        this.styleStack.push({ ...this.current });
        this.current = { ...this.current, color: attrs["value"] ?? this.current.color };
        return;
      case "size":
        this.styleStack.push({ ...this.current });
        this.current = {
          ...this.current,
          fontSize: this.computeSize(attrs["value"] ?? "0"),
        };
        return;
      case "shake":
        this.styleStack.push({ ...this.current });
        this.current = { ...this.current, shake: true };
        return;
      default:
        return;
    }
  }

  private handleCloseTag(): void {
    const popped = this.styleStack.pop();
    if (popped) this.current = popped;
  }

  private computeSize(value: string): number {
    if (/^[+-]?\d+$/.test(value)) {
      const n = parseInt(value, 10);
      const ratio = SIZE_MULTIPLIERS[n];
      if (ratio !== undefined) {
        return Math.max(8, Math.round(this.opts.baseSize * ratio));
      }
      return Math.max(8, n);
    }
    return this.opts.baseSize;
  }

  private async placeRuby(
    base: string,
    ruby: string,
    speedMs: number,
    skip: boolean,
  ): Promise<void> {
    if (!base) return;
    // ベース全体の幅を仮計測 → 入らなければ先に改行
    const probe = this.opts.scene.add.text(0, 0, base, {
      fontFamily: this.opts.fontFamily,
      fontSize: `${this.current.fontSize}px`,
      color: this.current.color,
    });
    const baseTotalWidth = probe.width;
    probe.destroy();
    if (this.cursorX > 0 && this.cursorX + baseTotalWidth > this.opts.maxWidth) {
      this.newline();
    }

    const startX = this.cursorX;
    const startY = this.cursorY;

    // ベース文字を配置(タイプライター)
    for (const ch of [...base]) {
      if (this.cancel) return;
      this.placeChar(ch);
      if (!this.fastFwd && !skip && speedMs > 0) {
        const pause = PUNCTUATION_PAUSE.includes(ch) ? speedMs * 4 : speedMs;
        await sleep(pause);
      }
    }

    if (!ruby) return;

    // ルビ配置(ベース中心から左右に展開)
    const baseWidth = baseTotalWidth;
    const rubyFontSize = Math.max(8, Math.round(this.current.fontSize * RUBY_SIZE));
    const rubyText = this.opts.scene.add.text(0, 0, ruby, {
      fontFamily: this.opts.fontFamily,
      fontSize: `${rubyFontSize}px`,
      color: this.opts.rubyColor ?? this.current.color,
    });
    if (this.opts.enableEdge !== false) {
      rubyText.setStroke(this.opts.edgeColor ?? "#1a1a2a", Math.max(1, (this.opts.edgeThickness ?? 3) - 1));
    }
    const rubyWidth = rubyText.width;
    const yOffset = this.opts.baseSize - this.current.fontSize;
    const rubyX = startX + (baseWidth - rubyWidth) / 2;
    const rubyY = startY + yOffset - rubyFontSize - 1;
    rubyText.setPosition(rubyX, rubyY);
    this.container.add(rubyText);
  }

  private shouldAddLeadSpace(segments: TextSegment[]): boolean {
    if (!this.opts.addConversationSpace) return false;
    for (const seg of segments) {
      if (seg.kind === "text" && seg.value.length > 0) {
        const firstCh = [...seg.value][0]!;
        return CONVERSATION_OPENERS.includes(firstCh);
      }
      if (seg.kind === "tag" && !seg.closing && seg.name === "ruby") {
        const base = seg.attrs["base"] ?? seg.attrs["_arg0"] ?? "";
        if (base.length > 0) {
          const firstCh = [...base][0]!;
          return CONVERSATION_OPENERS.includes(firstCh);
        }
      }
    }
    return false;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
